/**
 * Потребность против факта площадки по дням (РС-02, РС-06): люди и машины по графику и нормам — и сколько их было
 * на площадке по табелю, путевым листам и принятым отчётам смены; люди на вахте и план мобилизации.
 * Стоимость — только с уровнем денег (её присылает сервер).
 */
import { type DemandView, RESOURCE_TYPE_TITLES } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;
const n1 = (x: number) => (Math.round(x * 10) / 10).toLocaleString('ru-RU');

export function DemandPanel({ projectId }: { projectId: string }) {
  const [from, setFrom] = useState(today);
  const [days, setDays] = useState(14);
  const q = useQuery({
    queryKey: ['resource-demand', projectId, from, days],
    queryFn: () => api<DemandView>(`/projects/${projectId}/resource-demand`, { query: { from, days } }),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(from),
  });
  const now = today();
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <label htmlFor="demand-from" className="flex flex-col gap-1 text-[13px] text-ink-2">
          С даты
          <Input
            id="demand-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-auto max-md:h-11"
          />
        </label>
        <label htmlFor="demand-days" className="flex flex-col gap-1 text-[13px] text-ink-2">
          Дней
          <Select
            id="demand-days"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="max-md:h-11"
          >
            <option value={7}>7</option>
            <option value={14}>14</option>
            <option value={31}>31</option>
          </Select>
        </label>
        <span className="text-[13px] text-ink-2">
          В ячейке — нужно по графику и факт на площадке; красным — прошедший день, когда факта меньше
          потребности.
        </span>
      </div>
      {q.isPending && q.fetchStatus !== 'idle' ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {q.data && !q.data.rows.length ? (
        <EmptyState title="На эти дни потребности нет">
          Потребность считается из назначений людей и техники на работы графика, факт — из табеля, путевых
          листов и принятых отчётов смены.
        </EmptyState>
      ) : null}
      {q.data?.rows.length ? (
        <Panel bodyClassName="p-0">
          <ScrollX>
            <table className="text-[13px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  <th className="sticky left-0 z-10 min-w-[180px] bg-surface-2 py-2 pr-2 pl-4 font-medium">
                    Ресурс
                  </th>
                  {q.data.days.map((d) => (
                    <th key={d} className="num px-1.5 py-2 text-right font-medium whitespace-nowrap">
                      {dm(d)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((r) => (
                  <tr key={r.resourceId} className="border-t border-line">
                    <td className="sticky left-0 z-10 bg-surface py-1.5 pr-2 pl-4">
                      <div className="max-w-[220px] truncate" title={r.name}>
                        {r.name}
                      </div>
                      <div className="text-[11px] text-ink-2">{RESOURCE_TYPE_TITLES[r.kind]}</div>
                    </td>
                    {q.data.days.map((d, i) => {
                      const need = r.need[i]!;
                      const fact = r.fact ? r.fact[i]! : null;
                      const short = fact !== null && d < now && fact < Math.ceil(need - 0.05);
                      return (
                        <td
                          key={d}
                          className={clsx(
                            'num px-1.5 py-1.5 text-right whitespace-nowrap',
                            short
                              ? 'bg-danger-soft font-semibold text-danger'
                              : need || fact
                                ? ''
                                : 'text-ink-3',
                          )}
                          title={[
                            `нужно ${n1(need)}`,
                            fact !== null ? `факт ${fact}` : null,
                            r.onSite ? `на вахте ${r.onSite[i]}` : null,
                            r.planned ? `по плану мобилизации ${r.planned[i]}` : null,
                          ]
                            .filter(Boolean)
                            .join('; ')}
                        >
                          {need ? n1(need) : '·'}
                          {fact !== null ? (
                            <span className="block text-[10px] font-normal text-ink-2">факт {fact}</span>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {q.data.cost ? (
                  <tr className="border-t border-line bg-surface-2">
                    <td className="sticky left-0 z-10 bg-surface-2 py-1.5 pr-2 pl-4 text-[12px] text-ink-2">
                      Стоимость потребности
                    </td>
                    {q.data.cost.map((c, i) => (
                      <td
                        key={q.data.days[i]}
                        className="num px-1.5 py-1.5 text-right text-[12px] whitespace-nowrap"
                      >
                        {c ? tenge(c) : '·'}
                      </td>
                    ))}
                  </tr>
                ) : null}
              </tbody>
            </table>
          </ScrollX>
        </Panel>
      ) : null}
    </div>
  );
}
